import * as ReactIs from 'react-is';
import * as IDB from "idb-keyval";
import {Id as ToastId, toast, ToastContentProps} from "react-toastify";
import {IToastOptions, ToastTypes, UIUtilsEventTypes,} from "./type-declerations";
import {type ApiRequestDs, ApiResponseCodes, type ApiResponseDs, Texts, Utils} from '../../core';
import {
    ErrorToastContent,
    IApiResponseErrorToastContent,
    IApiResponseSuccessToastContent,
    IErrorToastContent,
    IInfoToastContent,
    InfoToastContent,
    ISuccessToastContent,
    IWarningToastContent,
    SuccessToastContent,
    WarningToastContent
} from '../components/toast-content';
import {ApiResponseErrorToastContent, ApiResponseSuccessToastContent} from "../index";
import type {SxProps, SystemStyleObject} from '@mui/system';
import {FunctionComponent} from "react";
import {EventEmitter} from "eventemitter3";


/**
 * Utilities specific for the UI of the project.
 */
class UIUtils extends EventEmitter<UIUtilsEventTypes> {

    // The prompt for the PWA installation.
    private static PWAInstallationPrompt: BeforeInstallPromptEvent | undefined = undefined;
    // The match media for the PWA.
    private static PWAMatch: MediaQueryList = window.matchMedia('(display-mode: standalone)');

    /**
     * The instance of the UIUtils.
     */
    public static readonly emitter = new UIUtils();

    /**
     * Determines if the PWA can be installed.
     * @return     Whether the PWA can be installed.
     */
    public static get PWAInstallable(): boolean {
        return !!this.PWAInstallationPrompt;
    }

    /**
     * Determines if the PWA is running.
     * @return     Whether the PWA is running.
     */
    public static get PWARunning(): boolean {
        return this.PWAMatch.matches;
    }

    /**
     * The prefix of the theme options.
     */
    public static readonly ThemePrefix = {
        default: 'bizkey',
        previewContent: 'bizkey-preview-content',
    };

    /**
     * The prefix of the theme options with the --. Specific for the variable names.
     * @param themePrefix   the prefix of the theme.
     * @return              the prefix with --.
     */
    public static withThemePrefix(themePrefix = this.ThemePrefix.default) {
        return '--'.concat(!themePrefix.length ? '' : themePrefix.concat('-'))
    }

    /**
     * The ids of the toast containers.
     */
    public static readonly ToastContainerIDs = {
        default: 'default',
        api: 'api',
    }

    /**
     * Creates a css variable with the theme-prefix.
     * @param name      the name of the variable.
     * @param themePrefix   the prefix of the theme.
     */
    public static themeVar(name: string, themePrefix = this.ThemePrefix.default) {
        return `var(${this.withThemePrefix(themePrefix)}${name})`;
    };

    /**
     * Shows toasts associated with the status of the api response if the permission was granted in the request.
     *
     * @param request       The request that was executed.
     * @param response      The response that was received.
     */
    public static showToastApiCallEffect(request: ApiRequestDs, response: ApiResponseDs): void {
        if (response?.resultFlag) {
            const message = response?.message ?? '';
            if (!!message?.length && request.showSuccessToast) {
                this.toast(ToastTypes.apiResponseSuccess, {
                    message: message,
                    title: response?.headerTitle,
                }, {toastId: request.url});
            }
            return;
        }
        if (!response?.aborted && request.showErrorToast) {
            // only show the toast for non-aborted responses.
            let message = response?.message ?? ApiResponseCodes.getMessageFromCode(response?.code) ?? Texts.serverError;
            this.toast(
                ToastTypes.apiResponseError,
                {
                    title: response?.headerTitle ?? "Error",
                    message: message,
                    submitTicket: ![ApiResponseCodes.unauthorized, ApiResponseCodes.internalUnauthorized].includes(response?.code ?? 0),
                    // setting default headers as CURL uses the headers
                    requestModel: response.baseResponse?.config ?? {...request, headers: {}},
                    responseModel: response,
                } as IApiResponseErrorToastContent,
                {toastId: request.method.concat(request.url)}
            );
        }
    }

    /**
     * Creates a screenshot of the user's browser window and sets it to the response.
     *
     * @param request       The request that was executed.
     * @param response      The response that was received.
     */
    public static async captureScreenshotApiCallEffect(request: ApiRequestDs, response: ApiResponseDs): Promise<void> {
        if (response?.resultFlag) {
            return;
        }
        response.viewScreenshot = await Utils.screenshotUserBrowserWindow();
    }

    /**
     * Invokes the given sx function with the given theme.
     * @param sx        the sx function to be invoked
     * @param theme     the theme to be passed to the sx function
     */
    public static invokeSx<Theme extends object>(sx?: SxProps<Theme>, theme?: Theme): SystemStyleObject<Theme> | ReadonlyArray<boolean | SystemStyleObject<Theme>> {
        if (!sx) {
            return {};
        }
        if (typeof sx === 'function') {
            if (!theme) {
                throw new Error('Theme must be provided when invoking a sx function.');
            }
            return sx(theme);
        }
        if (Array.isArray(sx)) {
            return sx.map(s => this.invokeSx(s, theme)) as ReadonlyArray<boolean | SystemStyleObject<Theme>>;
        }
        return sx as SystemStyleObject<Theme>;
    }

    /**
     * Determines if the given object is a valid element for the React to render.
     * @param obj
     */
    public static isObjectValidElementType(obj: object): boolean {
        if (obj === null)
            return false;
        return ReactIs.isValidElementType(obj);
    }

    /**
     * Captures the PWA installation prompt.
     */
    public static capturePWAInstallationPrompt() {
        window.addEventListener('beforeinstallprompt', async (e) => {
            this.PWAInstallationPrompt = e;
            await IDB.set('pwa-installed', false);
            this.emitter.emit('pwa-installable', true);
        });
        window.addEventListener('appinstalled', async () => {
            this.PWAInstallationPrompt = undefined;
            await IDB.set('pwa-installed', true);
            this.emitter.emit('pwa-installable', false);
        });
    }

    /**
     * Listens to the event of the PWA installation prompt.
     * @param callback    the callback to be invoked when the event is emitted.
     * @return            the function to be invoked to remove the listener.
     */
    public static onPWAInstallable(callback: EventEmitter.EventListener<UIUtilsEventTypes, 'pwa-installable'>) {
        this.emitter.on('pwa-installable', callback);
        this.emitter.emit('pwa-installable', this.PWAInstallable);
        return () => {
            this.emitter.off('pwa-installable', callback)
        };
    }

    /**
     * Listens to the event of the PWA running.
     * @param callback      the callback to be invoked when the event is emitted.
     */
    public static onPWARunning(callback: EventEmitter.EventListener<UIUtilsEventTypes, 'pwa-running'>) {
        const listener: (ev: MediaQueryListEvent) => any = (evt) => {
            let displayMode = 'browser';
            if (evt.matches) {
                displayMode = 'standalone';
            }
            this.emitter.emit('pwa-running', displayMode === 'standalone');
        }
        this.PWAMatch.addEventListener('change', listener);
        this.emitter.on('pwa-running', callback);
        this.emitter.emit('pwa-running', this.PWARunning);
        return () => {
            this.PWAMatch.removeEventListener('change', listener);
            this.emitter.off('pwa-running', callback);
        }
    }

    /**
     * Prompts the user to install the PWA.
     */
    public static async promptPWAInstallation() {
        if (!!this.PWAInstallationPrompt) {
            const choice = await this.PWAInstallationPrompt.prompt();
            if (choice.outcome === 'accepted') {
                this.PWAInstallationPrompt = undefined;
                await IDB.set('pwa-installed', true);
                this.emitter.emit('pwa-installable', false);
            }
            this.PWAInstallationPrompt?.userChoice.then((choice) => {
                if (choice.outcome === 'accepted') {
                    this.PWAInstallationPrompt = undefined;
                    IDB.set('pwa-installed', true);
                    this.emitter.emit('pwa-installable', false);
                }
            })
        } else {
            this.toast(ToastTypes.error, {
                title: 'PWA Installation',
                message: `PWA installation prompt is not available. Please ensure you are using the latest version of Google Chrome as your 
                Browser as this feature is has limited support on other browsers.`,
            })
        }
    }

    /**
     * Shows the toast for duplicateCode toast-type.
     * @param type      the type of the toast that determines the content to be shown.
     * @param data      the data specific for the toast content.
     * @param options   the options related to the toast itself.
     */
    public static toast(type: ToastTypes.apiResponseError, data: IApiResponseErrorToastContent, options?: IToastOptions): ToastId;
    /**
     * Shows the toast for apiResponseSuccess toast-type.
     * @param type      the type of the toast that determines the content to be shown.
     * @param data      the data specific for the toast content.
     * @param options   the options related to the toast itself.
     */
    public static toast(type: ToastTypes.apiResponseSuccess, data: IApiResponseSuccessToastContent, options?: IToastOptions): ToastId;
    /**
     * Shows the toast for error toast-type.
     * @param type      the type of the toast that determines the content to be shown.
     * @param data      the data specific for the toast content.
     * @param options   the options related to the toast itself.
     */
    public static toast(type: ToastTypes.error, data: IErrorToastContent, options?: IToastOptions): ToastId;
    /**
     * Shows the toast for info toast-type.
     * @param type      the type of the toast that determines the content to be shown.
     * @param data      the data specific for the toast content.
     * @param options   the options related to the toast itself.
     */
    public static toast(type: ToastTypes.info, data: IInfoToastContent, options?: IToastOptions): ToastId;
    /**
     * Shows the toast for success toast-type.
     * @param type      the type of the toast that determines the content to be shown.
     * @param data      the data specific for the toast content.
     * @param options   the options related to the toast itself.
     */
    public static toast(type: ToastTypes.success, data: ISuccessToastContent, options?: IToastOptions): ToastId;
    /**
     * Shows the toast for warning toast-type.
     * @param type      the type of the toast that determines the content to be shown.
     * @param data      the data specific for the toast content.
     * @param options   the options related to the toast itself.
     */
    public static toast(type: ToastTypes.warning, data: IWarningToastContent, options?: IToastOptions): ToastId;
    /**
     * Shows a toast specific for the given [type].
     * @param type      the type of the toast that determines the content to be shown.
     * @param data      the data specific for the toast content.
     * @param options   the options related to the toast itself.
     */
    public static toast(type: ToastTypes, data?: any, options?: IToastOptions): ToastId {
        options = options ?? {};
        let Content: FunctionComponent<ToastContentProps<any>>;
        let containerId;
        switch (type) {
            case ToastTypes.apiResponseError:
                Content = ApiResponseErrorToastContent;
                containerId = this.ToastContainerIDs.api;
                break;
            case ToastTypes.apiResponseSuccess:
                Content = ApiResponseSuccessToastContent;
                containerId = this.ToastContainerIDs.api;
                break;
            case ToastTypes.error:
                Content = ErrorToastContent;
                containerId = this.ToastContainerIDs.default;
                break
            case ToastTypes.info:
                Content = InfoToastContent;
                containerId = this.ToastContainerIDs.default;
                break
            case ToastTypes.success:
                Content = SuccessToastContent;
                containerId = this.ToastContainerIDs.default;
                break
            case ToastTypes.warning:
                Content = WarningToastContent;
                containerId = this.ToastContainerIDs.default;
                break
        }
        const certainContainerId = options?.containerId ?? containerId ?? this.ToastContainerIDs.default;
        return toast<any>((props) => <Content {...props}/>, {
            ...options,
            containerId: certainContainerId,
            data: data,
            ...([ToastTypes.apiResponseError].includes(type) ? {
                autoClose: false,
            } : {})
        });
    }
}

export * from './type-declerations';
export default UIUtils;
